// R2 upload helper used by the admin ImageUpload component and any
// future toolbar that needs to push a single file to R2 (e.g. rich-text
// image button). Mirrors miluliem's helper 1:1 — adjust there first if
// behaviour needs to change in either project.

import { processImage } from "./image-processing";

const MEDIA_UPLOAD_URL = import.meta.env.VITE_MEDIA_UPLOAD_URL || "";
const MEDIA_UPLOAD_SECRET = import.meta.env.VITE_MEDIA_UPLOAD_SECRET || "";

export type UploadBucket =
  | "products"
  | "categories"
  | "banners"
  | "collections"
  | "blog"
  | "tags";

/** Buckets that store binary documents (PDF spec sheets, video clips, etc.)
 * — skip the image-processing pipeline and upload as-is. */
export type DocumentBucket = "documents";

interface UploadOptions {
  bucket: UploadBucket;
  /** Skip the square-crop pass when the natural aspect ratio matters —
   *  i.e. any full-bleed background art: banners, collection covers, blog
   *  cover images and category tiles. Leaving it off runs the product-mode
   *  pass, which pads the image onto a white square and bakes visible bars
   *  into the file. */
  preserveAspectRatio?: boolean;
}

/**
 * Upload a single image to R2 via the vinoga-media-upload Worker.
 * Returns the public URL of the stored image. Throws on failure.
 */
export async function uploadImageToR2(
  file: File,
  { bucket, preserveAspectRatio = false }: UploadOptions,
): Promise<string> {
  if (!MEDIA_UPLOAD_URL || !MEDIA_UPLOAD_SECRET) {
    throw new Error(
      "R2 upload not configured. Set VITE_MEDIA_UPLOAD_URL and VITE_MEDIA_UPLOAD_SECRET in admin env.",
    );
  }

  const blob = await processImage(file, { preserveAspectRatio });
  const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.webp`;
  const key = `${bucket}/${fileName}`;

  const res = await fetch(`${MEDIA_UPLOAD_URL}/upload/${key}`, {
    method: "PUT",
    headers: {
      "Content-Type": "image/webp",
      Authorization: `Bearer ${MEDIA_UPLOAD_SECRET}`,
    },
    body: blob,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      message = data?.error || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }
  const data = await res.json();
  if (!data?.url) throw new Error("Upload succeeded but the worker returned no URL.");
  return data.url as string;
}

interface UploadFileOptions {
  bucket: DocumentBucket;
}

/**
 * Upload a non-image binary (PDF spec sheets, video clips) to R2 via the
 * vinoga-media-upload Worker. Skips image processing — the file is sent
 * unmodified with its native MIME type. Returns the public URL.
 */
export async function uploadFileToR2(
  file: File,
  { bucket }: UploadFileOptions,
): Promise<string> {
  if (!MEDIA_UPLOAD_URL || !MEDIA_UPLOAD_SECRET) {
    throw new Error(
      "R2 upload not configured. Set VITE_MEDIA_UPLOAD_URL and VITE_MEDIA_UPLOAD_SECRET in admin env.",
    );
  }

  // Preserve the original extension so the public URL is something a human
  // can read (`spec-sheet.pdf` rather than an opaque random blob).
  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex >= 0 ? file.name.slice(dotIndex) : "";
  const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}${ext}`;
  const key = `${bucket}/${fileName}`;

  const res = await fetch(`${MEDIA_UPLOAD_URL}/upload/${key}`, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      Authorization: `Bearer ${MEDIA_UPLOAD_SECRET}`,
    },
    body: file,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      message = data?.error || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }
  const data = await res.json();
  if (!data?.url) throw new Error("Upload succeeded but the worker returned no URL.");
  return data.url as string;
}
